import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, Search, Home, Ticket, User, LogIn, LogOut, Settings, Wallet, Calendar, ScanLine, PlusCircle } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { LanguageSwitcher } from "../LanguageSwitcher";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "../ui/dropdown-menu";

export const Navbar = () => {
    const { t } = useTranslation();
    const { isAuthenticated, profile, signOut } = useAuth();
    const isStaff = !!profile?.is_staff;
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();

    const handleLogout = async () => {
        await signOut();
        navigate("/");
    };

    return (
        <>
            <header className="sticky top-0 z-30 glass-header" data-testid="navbar">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                    <Link to="/" data-testid="navbar-logo" className="flex items-center shrink-0">
                        <img src="/logo-youvibeticket-clr.png" alt="youvibeticket" className="h-8 md:h-9 w-auto" />
                    </Link>

                    <nav className="hidden md:flex items-center gap-6">
                        <NavLink
                            to="/"
                            end
                            data-testid="nav-home"
                            className={({ isActive }) =>
                                `text-sm font-semibold transition-colors ${isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`
                            }
                        >
                            {t("nav.home")}
                        </NavLink>
                        <NavLink
                            to="/search"
                            data-testid="nav-search"
                            className={({ isActive }) =>
                                `text-sm font-semibold transition-colors ${isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`
                            }
                        >
                            {t("nav.search")}
                        </NavLink>
                        {isAuthenticated && (
                            <NavLink
                                to="/bookings"
                                data-testid="nav-bookings"
                                className={({ isActive }) =>
                                    `text-sm font-semibold transition-colors ${isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`
                                }
                            >
                                {t("nav.bookings")}
                            </NavLink>
                        )}
                    </nav>

                    <div className="flex items-center gap-2 md:gap-3">
                        <LanguageSwitcher variant="minimal" />
                        {isAuthenticated ? (
                            <div className="hidden md:flex items-center gap-2">
                                {profile?.is_organizer && (
                                    <Link
                                        to="/organizer/listings"
                                        data-testid="nav-organizer"
                                        className="text-sm font-semibold px-4 py-2 rounded-full hover:bg-muted transition-colors"
                                    >
                                        {t("nav.my_listings")}
                                    </Link>
                                )}
                                {profile?.is_admin && (
                                    <Link
                                        to="/admin"
                                        data-testid="nav-admin"
                                        className="text-sm font-semibold px-4 py-2 rounded-full bg-foreground text-background hover:opacity-90 transition-opacity"
                                    >
                                        {t("nav.admin")}
                                    </Link>
                                )}
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <button
                                            data-testid="nav-profile-menu"
                                            className="p-2 rounded-full hover:bg-muted transition-colors flex items-center gap-2"
                                        >
                                            <div className="w-8 h-8 rounded-full bg-foreground text-background flex items-center justify-center text-xs font-bold">
                                                {(profile?.first_name?.[0] || profile?.email?.[0] || "?").toUpperCase()}
                                            </div>
                                        </button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="min-w-[200px]">
                                        <div className="px-2 py-1.5 text-xs text-muted-foreground truncate">
                                            {profile?.email}
                                        </div>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem asChild>
                                            <Link to="/profile" data-testid="menu-profile" className="cursor-pointer flex items-center gap-2">
                                                <User className="h-4 w-4" />
                                                {t("nav.profile")}
                                            </Link>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem asChild>
                                            <Link to="/bookings" data-testid="menu-bookings" className="cursor-pointer flex items-center gap-2">
                                                <Ticket className="h-4 w-4" />
                                                {t("nav.bookings")}
                                            </Link>
                                        </DropdownMenuItem>
                                        {isStaff && (
                                            <DropdownMenuItem asChild>
                                                <Link to="/staff" data-testid="menu-staff" className="cursor-pointer flex items-center gap-2">
                                                    <ScanLine className="h-4 w-4" />
                                                    {t("nav.checkin")}
                                                </Link>
                                            </DropdownMenuItem>
                                        )}
                                        {profile?.is_organizer && (
                                            <>
                                                <DropdownMenuItem asChild>
                                                    <Link to="/organizer/listings" data-testid="menu-listings" className="cursor-pointer flex items-center gap-2">
                                                        <Calendar className="h-4 w-4" />
                                                        {t("nav.my_listings")}
                                                    </Link>
                                                </DropdownMenuItem>
                                                <DropdownMenuItem asChild>
                                                    <Link to="/organizer/create-event" data-testid="menu-create-event" className="cursor-pointer flex items-center gap-2">
                                                        <PlusCircle className="h-4 w-4" />
                                                        {t("nav.create_event")}
                                                    </Link>
                                                </DropdownMenuItem>
                                                <DropdownMenuItem asChild>
                                                    <Link to="/organizer/wallet" data-testid="menu-wallet" className="cursor-pointer flex items-center gap-2">
                                                        <Wallet className="h-4 w-4" />
                                                        {t("nav.wallet")}
                                                    </Link>
                                                </DropdownMenuItem>
                                            </>
                                        )}
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem
                                            data-testid="menu-logout"
                                            onClick={handleLogout}
                                            className="cursor-pointer flex items-center gap-2 text-destructive"
                                        >
                                            <LogOut className="h-4 w-4" />
                                            {t("nav.logout")}
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>
                        ) : (
                            <div className="hidden md:flex items-center gap-2">
                                <Link
                                    to="/login"
                                    data-testid="nav-login"
                                    className="text-sm font-semibold px-4 py-2 hover:text-muted-foreground transition-colors"
                                >
                                    {t("nav.signin")}
                                </Link>
                                <Link
                                    to="/register"
                                    data-testid="nav-register"
                                    className="text-sm font-semibold px-5 py-2.5 rounded-full bg-foreground text-background hover:opacity-90 transition-opacity"
                                >
                                    {t("nav.signup")}
                                </Link>
                            </div>
                        )}
                        <button
                            data-testid="navbar-menu-btn"
                            onClick={() => setOpen(!open)}
                            className="md:hidden p-2 rounded-full hover:bg-muted transition-colors"
                        >
                            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                        </button>
                    </div>
                </div>

                {/* Mobile menu */}
                {open && (
                    <div className="md:hidden border-t border-border bg-white" data-testid="mobile-menu">
                        <div className="px-4 py-3 space-y-1">
                            <Link
                                to="/"
                                onClick={() => setOpen(false)}
                                className="block px-3 py-2 rounded-lg text-sm font-semibold hover:bg-muted"
                            >
                                {t("nav.home")}
                            </Link>
                            <Link
                                to="/search"
                                onClick={() => setOpen(false)}
                                className="block px-3 py-2 rounded-lg text-sm font-semibold hover:bg-muted"
                            >
                                {t("nav.search")}
                            </Link>
                            {isAuthenticated ? (
                                <>
                                    <Link
                                        to="/bookings"
                                        onClick={() => setOpen(false)}
                                        className="block px-3 py-2 rounded-lg text-sm font-semibold hover:bg-muted"
                                    >
                                        {t("nav.bookings")}
                                    </Link>
                                    <Link
                                        to="/profile"
                                        onClick={() => setOpen(false)}
                                        className="block px-3 py-2 rounded-lg text-sm font-semibold hover:bg-muted"
                                    >
                                        {t("nav.profile")}
                                    </Link>
                                    {isStaff && (
                                        <Link
                                            to="/staff"
                                            onClick={() => setOpen(false)}
                                            data-testid="mobile-menu-staff"
                                            className="block px-3 py-2 rounded-lg text-sm font-semibold hover:bg-muted"
                                        >
                                            {t("nav.checkin")}
                                        </Link>
                                    )}
                                    {profile?.is_organizer && (
                                        <>
                                            <Link
                                                to="/organizer/listings"
                                                onClick={() => setOpen(false)}
                                                className="block px-3 py-2 rounded-lg text-sm font-semibold hover:bg-muted"
                                            >
                                                {t("nav.my_listings")}
                                            </Link>
                                            <Link
                                                to="/organizer/create-event"
                                                onClick={() => setOpen(false)}
                                                data-testid="mobile-menu-create-event"
                                                className="block px-3 py-2 rounded-lg text-sm font-semibold hover:bg-muted"
                                            >
                                                {t("nav.create_event")}
                                            </Link>
                                            <Link
                                                to="/organizer/wallet"
                                                onClick={() => setOpen(false)}
                                                className="block px-3 py-2 rounded-lg text-sm font-semibold hover:bg-muted"
                                            >
                                                {t("nav.wallet")}
                                            </Link>
                                        </>
                                    )}
                                    {profile?.is_admin && (
                                        <Link
                                            to="/admin"
                                            onClick={() => setOpen(false)}
                                            className="block px-3 py-2 rounded-lg text-sm font-semibold hover:bg-muted"
                                        >
                                            {t("nav.admin")}
                                        </Link>
                                    )}
                                    <button
                                        onClick={async () => {
                                            await signOut();
                                            setOpen(false);
                                            navigate("/");
                                        }}
                                        data-testid="mobile-logout-btn"
                                        className="block w-full text-left px-3 py-2 rounded-lg text-sm font-semibold hover:bg-muted text-destructive"
                                    >
                                        {t("nav.logout")}
                                    </button>
                                </>
                            ) : (
                                <>
                                    <Link
                                        to="/login"
                                        onClick={() => setOpen(false)}
                                        className="block px-3 py-2 rounded-lg text-sm font-semibold hover:bg-muted"
                                    >
                                        {t("nav.signin")}
                                    </Link>
                                    <Link
                                        to="/register"
                                        onClick={() => setOpen(false)}
                                        className="block px-3 py-2 rounded-lg text-sm font-semibold hover:bg-muted"
                                    >
                                        {t("nav.signup")}
                                    </Link>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </header>
        </>
    );
};

export const MobileBottomNav = () => {
    const { t } = useTranslation();
    const { isAuthenticated } = useAuth();

    return (
        <nav className="mobile-nav" data-testid="mobile-bottom-nav">
            <NavLink
                to="/"
                end
                data-testid="mobile-nav-home"
                className={({ isActive }) => `mobile-nav-item ${isActive ? "active" : ""}`}
            >
                <Home className="h-5 w-5" />
                <span>{t("nav.home")}</span>
            </NavLink>
            <NavLink
                to="/search"
                data-testid="mobile-nav-search"
                className={({ isActive }) => `mobile-nav-item ${isActive ? "active" : ""}`}
            >
                <Search className="h-5 w-5" />
                <span>{t("nav.search")}</span>
            </NavLink>
            {isAuthenticated ? (
                <>
                    <NavLink
                        to="/bookings"
                        data-testid="mobile-nav-bookings"
                        className={({ isActive }) => `mobile-nav-item ${isActive ? "active" : ""}`}
                    >
                        <Ticket className="h-5 w-5" />
                        <span>{t("nav.bookings")}</span>
                    </NavLink>
                    <NavLink
                        to="/profile"
                        data-testid="mobile-nav-profile"
                        className={({ isActive }) => `mobile-nav-item ${isActive ? "active" : ""}`}
                    >
                        <User className="h-5 w-5" />
                        <span>{t("nav.profile")}</span>
                    </NavLink>
                </>
            ) : (
                <>
                    <NavLink
                        to="/login"
                        data-testid="mobile-nav-login"
                        className={({ isActive }) => `mobile-nav-item ${isActive ? "active" : ""}`}
                    >
                        <LogIn className="h-5 w-5" />
                        <span>{t("nav.signin")}</span>
                    </NavLink>
                    <NavLink
                        to="/register"
                        data-testid="mobile-nav-register"
                        className={({ isActive }) => `mobile-nav-item ${isActive ? "active" : ""}`}
                    >
                        <User className="h-5 w-5" />
                        <span>{t("nav.signup")}</span>
                    </NavLink>
                </>
            )}
        </nav>
    );
};
